'use client'

import * as React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { cn } from 'lib/utils'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../../firebase'

const parseAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]+/g, '')
    const parsed = Number(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const getIndicatorTarget = (indicator) => {
  if (Array.isArray(indicator.targets) && indicator.targets.length > 0) {
    return parseAmount(indicator.targets[0]?.value)
  }
  return parseAmount(indicator.target || indicator.targetValue || 0)
}

const getIndicatorActual = (indicator) =>
  parseAmount(
    indicator.actual ??
      indicator.currentValue ??
      indicator.current ??
      indicator.value ??
      indicator.progress ??
      0,
  )

const getIndicatorRate = (indicator) => {
  const target = getIndicatorTarget(indicator) || 0
  const actual = getIndicatorActual(indicator)
  if (!target) return 0
  return Math.round((actual / target) * 100)
}

const getEvidenceType = (evidence) => {
  const mime = (evidence?.type || '').toLowerCase()
  const name = (evidence?.name || '').toLowerCase()
  if (mime.startsWith('image/') || name.match(/\.(png|jpg|jpeg|gif|webp)$/)) return 'photo'
  if (mime.includes('csv') || mime.includes('spreadsheet') || mime.includes('excel') || name.endsWith('.csv')) return 'dataset'
  if (name.includes('report')) return 'report'
  if (name.includes('survey')) return 'survey'
  return 'document'
}

export default function OutputsDashboard() {
  const [projects, setProjects] = React.useState([])
  const [indicators, setIndicators] = React.useState([])
  const [evidenceRecords, setEvidenceRecords] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadOutputs() {
      try {
        const [projectSnap, indicatorSnap] = await Promise.all([
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'indicators')),
        ])
        if (!isMounted) return
        const projectList = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const indicatorList = indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const evidence = indicatorList
          .filter((indicator) => indicator.evidence?.url)
          .map((indicator) => ({
            id: indicator.id,
            verified: Boolean(indicator.evidence?.verified),
            type: getEvidenceType(indicator.evidence),
          }))
        setProjects(projectList)
        setIndicators(indicatorList)
        setEvidenceRecords(evidence)
      } catch {
        if (!isMounted) return
        setProjects([])
        setIndicators([])
        setEvidenceRecords([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadOutputs()

    return () => {
      isMounted = false
    }
  }, [])

  const outputIndicators = indicators.filter((indicator) => String(indicator.level || '').toLowerCase() === 'output')
  const outcomeIndicators = indicators.filter((indicator) => String(indicator.level || '').toLowerCase() === 'outcome')
  const goalIndicators = indicators.filter((indicator) => String(indicator.level || '').toLowerCase() === 'goal')

  const outputsByProject = projects.map((project) => {
    const projectIndicators = outputIndicators.filter((indicator) => indicator.projectId === project.id)
    const achieved = projectIndicators.filter((indicator) => getIndicatorRate(indicator) >= 80).length
    const total = projectIndicators.length
    return {
      project: project.code || project.shortCode || project.title || 'Project',
      title: project.title || 'Project',
      achieved,
      total,
      rate: total > 0 ? Math.round((achieved / total) * 100) : 0,
    }
  })

  const outputPerformance = outputsByProject.map((project) => ({
    project: project.project,
    achieved: project.achieved,
    remaining: project.total - project.achieved,
  }))

  const levelSummary = [
    {
      name: 'Outputs',
      onTrack: outputIndicators.filter((indicator) => getIndicatorRate(indicator) >= 80).length,
      atRisk: outputIndicators.filter((indicator) => {
        const rate = getIndicatorRate(indicator)
        return rate >= 50 && rate < 80
      }).length,
      offTrack: outputIndicators.filter((indicator) => getIndicatorRate(indicator) < 50).length,
    },
    {
      name: 'Outcomes',
      onTrack: outcomeIndicators.filter((indicator) => getIndicatorRate(indicator) >= 80).length,
      atRisk: outcomeIndicators.filter((indicator) => {
        const rate = getIndicatorRate(indicator)
        return rate >= 50 && rate < 80
      }).length,
      offTrack: outcomeIndicators.filter((indicator) => getIndicatorRate(indicator) < 50).length,
    },
    {
      name: 'Goals',
      onTrack: goalIndicators.filter((indicator) => getIndicatorRate(indicator) >= 80).length,
      atRisk: goalIndicators.filter((indicator) => {
        const rate = getIndicatorRate(indicator)
        return rate >= 50 && rate < 80
      }).length,
      offTrack: goalIndicators.filter((indicator) => getIndicatorRate(indicator) < 50).length,
    },
  ]

  const evidenceByType = [
    { name: 'Documents', value: evidenceRecords.filter((record) => record.type === 'document').length, color: 'oklch(0.35 0.12 145)' },
    { name: 'Photos', value: evidenceRecords.filter((record) => record.type === 'photo').length, color: 'oklch(0.55 0.16 150)' },
    { name: 'Datasets', value: evidenceRecords.filter((record) => record.type === 'dataset').length, color: 'oklch(0.65 0.15 150)' },
    { name: 'Reports', value: evidenceRecords.filter((record) => record.type === 'report').length, color: 'oklch(0.87 0.15 85)' },
    { name: 'Surveys', value: evidenceRecords.filter((record) => record.type === 'survey').length, color: 'oklch(0.7 0.17 65)' },
  ]

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Outputs Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Output delivery tracking, results achievement, and evidence documentation</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : outputIndicators.length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Output Indicators</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : outcomeIndicators.length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Outcome Indicators</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : outputIndicators.filter((indicator) => getIndicatorRate(indicator) >= 80).length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Outputs Achieved</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : evidenceRecords.length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Evidence Items</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : evidenceRecords.filter((record) => record.verified).length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Verified</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Indicator Performance by Level</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={levelSummary} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="onTrack" stackId="a" fill="oklch(0.55 0.16 150)" name="On Track" />
                <Bar dataKey="atRisk" stackId="a" fill="oklch(0.87 0.15 85)" name="At Risk" />
                <Bar dataKey="offTrack" stackId="a" fill="oklch(0.577 0.245 27.325)" name="Off Track" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Output Achievement by Project</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={outputPerformance} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="project" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="achieved" stackId="a" fill="oklch(0.55 0.16 150)" name="Achieved" />
                <Bar dataKey="remaining" stackId="a" fill="oklch(0.9 0.01 145)" name="Remaining" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Evidence by Type</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={evidenceByType}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                  dataKey="value"
                  paddingAngle={3}
                  strokeWidth={0}
                >
                  {evidenceByType.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
              {evidenceByType.map((entry) => (
                <div key={entry.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-[10px] text-muted-foreground">
                    {entry.name} ({entry.value})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Output Cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {loading ? (
          <Card className="border border-border lg:col-span-2">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">Loading outputs...</CardContent>
          </Card>
        ) : outputsByProject.length === 0 ? (
          <Card className="border border-border lg:col-span-2">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">No output indicators found.</CardContent>
          </Card>
        ) : (
          outputsByProject.map((project) => (
            <Card key={project.project} className="border border-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{project.title}</h3>
                    <p className="text-[10px] text-muted-foreground">{project.project} | {project.total} output indicators</p>
                  </div>
                  <Badge className={cn('text-[10px]', project.rate >= 80 ? 'bg-success/15 text-success' : project.rate >= 50 ? 'bg-warning/15 text-warning' : 'bg-destructive/15 text-destructive')}>
                    {project.rate}% achieved
                  </Badge>
                </div>
                <Progress value={project.rate} className="h-2 mb-2" />
                <div className="flex items-center gap-4">
                  <span className="text-[10px] text-success font-medium">{project.achieved} on track</span>
                  <span className="text-[10px] text-muted-foreground">{project.total - project.achieved} remaining</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
